/*
 * Copyright (c) 2016 Omnigon Communications, LLC. All rights reserved.
 *
 * This software is the confidential and proprietary information of Omnigon Communications, LLC
 * ("Confidential Information"). You shall not disclose such Confidential Information and shall access and use it only
 * in accordance with the terms of the license agreement you entered into with Omnigon Communications, LLC, its
 * subsidiaries, affiliates or authorized licensee. Unless required by applicable law or agreed to in writing, this
 * Confidential Information is provided on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either
 * express or implied. See the license agreement for the specific language governing permissions and limitations.
 */

import Component from 'can/component/component';
import _ from 'lodash';
import can from 'can';
import DomEventsManager from 'scripts/dom-events-manager';
import FeedService from 'scripts/services/base-feed-service';
import datetimeUtil from 'scripts/utils/date-time';
import dtm from 'scripts/services/dtm';
import drawerView from './leaderboard-drawer.stache';
import appContext from 'scripts/app-context';

import template from './leaderboard.stache';

const scoringLeaderboardFeed = window.globalSettings.feeds.history.scoring.scoring;
const subscriptionKey = window.globalSettings.feeds.history.subscriptionKey;

const lettersRegexp = /[^\d]+/g;

const priority = ['CUT', 'MC', 'WD', 'DQ'];

const scoringSortFn = function (a, b) {
    const apos = a.attr('pos').toString();
    const bpos = b.attr('pos').toString();

    let ac = a.attr('pos') ? +apos.replace(lettersRegexp, '') : '-';
    let bc = b.attr('pos') ? +bpos.replace(lettersRegexp, '') : '-';

    if (!ac) {
        ac = priority.indexOf(apos) + 999;
    }
    if (!bc) {
        bc = priority.indexOf(bpos) + 999;
    }

    if (ac === bc) {
        return a.attr('totalScore') > b.attr('totalScore') ? 1 : -1;
    }
    return ac > bc ? 1 : -1;
};

Component.extend({
    tag: 'history-experience-scoring-leaderboard',
    template: template,
    leakScope: false,
    lazyLoadingDisabled: true,
    activeDrawer: null,
    viewModel: {
        define: {
            fetching: {
                type: 'boolean',
                value: true,
            },
            fethingFailed: {
                type: 'boolean',
                value: false,
            },
            players: {
                Type: Map.List,
                Value: Map.List,
            },
            legend: {
                type: 'string',
                value: '',
            },
            currentYear: {
                type: 'number',
                value: new Date().getFullYear(),
            },
            activeTab: {
                Type: can.Map,
                Value: can.Map,
            },
        },
        getLegend(currentYear) {
            const years = this.attr('settings.legendYears');
            if (
                years &&
                currentYear &&
                years.indexOf(currentYear.toString()) != -1
            ) {
                return this.attr('settings.legendYearText');
            }
            return false;
        },
    },
    events: {
        inserted() {
            this.fetchData();

            appContext.model.bind(
                'history-experience-scoring-and-stats-years-change',
                this.handleYearChange.bind(this),
            );

            this.viewModel.bind('yearSelected', (ev, yearSelected) => {
                this.fetchData(yearSelected);
            });
            DomEventsManager.subscribe(this);
        },

        removed() {
            DomEventsManager.unsubscribe(this);
        },

        setHolesThroughSortingDate(holesThrough) {
            if (holesThrough.displayValue) {
                return datetimeUtil.getTimeDiffFromNightInStr(
                    holesThrough.displayValue,
                );
            } else {
                return '';
            }
        },

        fetchData(identifier = 'meta') {
            const headers = subscriptionKey
                ? {['Ocp-Apim-Subscription-Key']: subscriptionKey}
                : {};

            this.feedService = new FeedService({
                    feedUrl: scoringLeaderboardFeed,
                    fromCache: false,
                    autoUpdate: false,
                    headers
                }
            );
            this.viewModel.attr('fetchingFailed', false);

            this.feedService
                .on('fetching', (ev, value) => {
                    if (identifier !== 'meta') {
                        this.viewModel.attr('fetching', value);
                    }
                })
                .on('data', (ev, data) => {
                    if (identifier === 'meta') {
                        appContext.model.dispatch(
                            'history-experience-scoring-and-stats-years-ready',
                            [data.years],
                        );
                    } else {
                        let players = _.get(data, 'players', []);
                        can.batch.start();
                        players.forEach((player) => {
                            player.roundPars = [];
                            if (player.rounds) {
                                player.rounds.forEach((round) => {
                                    let roundPar = 0;
                                    if (round.holes) {
                                        round.holes.forEach((hole) => {
                                            roundPar += hole.par;
                                        });
                                    }
                                    player.roundPars.push(roundPar);
                                });
                            }
                        });
                        this.viewModel.attr('players', players);
                        this.viewModel
                            .attr('players')
                            .attr('comparator', scoringSortFn);
                        this.viewModel.attr('legend', data.legend);
                        can.batch.stop();
                    }
                })
                .on('error', (ev, data) => {
                    this.viewModel.attr('fetching', false);
                    this.viewModel.attr('fetchingFailed', true);
                });

            this.feedService.fetch({identifier: identifier});
        },

        toggleDrawer($el) {
            let identifier = $el.data('identifier');
            let playerName = $el.data('name');
            let activeDrawerIdentifier = null;

            if (this.activeDrawer) {
                activeDrawerIdentifier = this.activeDrawer.attr(
                    'data-identifier',
                );
                this.removeDrawer();
            } else {
                dtm.trackLeaderboardExpand(playerName);
            }
            if (activeDrawerIdentifier != identifier) {
                let data = this.viewModel.players.filter(
                    (item) => item.id == identifier,
                )[0];
                if (data) {
                    $el.after(
                        drawerView({
                            data: data,
                            removeDrawer: this.removeDrawer.bind(this),
                        }),
                    );
                    this.activeDrawer = $el.next();
                }
            }
        },

        removeDrawer() {
            if (this.activeDrawer) {
                this.activeDrawer.remove();
                this.activeDrawer = null;
            }
        },

        handleYearChange(event, year) {
            this.viewModel.attr('yearSelected', year);
            this.fetchData(year);
        },

        '.js-board-row click'(element, event) {
            this.toggleDrawer(element);
        },
    },
});
